import {
  DROPDOWN_ITEMS_MAP,
  TOOLBAR_TOOLS
} from "./chunk-JT4U45DL.js";
import {
  ItemTypes
} from "./chunk-IX3ZB4XQ.js";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "./chunk-OERUTHPZ.js";
import {
  PanelCloseButton
} from "./chunk-W22Y52AU.js";
import {
  Button
} from "./chunk-QXEA2YZ6.js";
import {
  Icon
} from "./chunk-7IEFGXFA.js";
import {
  cn
} from "./chunk-SBEJSA5Q.js";

// src/components/canvas/toolbar/canvas-toolbar.tsx
import { useContext, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DndContext, useDrag } from "react-dnd";
import { jsx, jsxs } from "react/jsx-runtime";
var RAIL_WIDTH = 48;
var CATALOG_WIDTH = 272;
var PARAMETERS_WIDTH = 360;
function sectionsFor(tools, items) {
  return tools.flatMap((tool) => {
    const sections = [];
    const visit = (entries, path, id) => {
      const leaves = entries.filter((entry) => !entry.children?.length);
      if (leaves.length) sections.push({ id, category: tool.id, label: path.join(" \xB7 "), items: leaves });
      for (const entry of entries) if (entry.children?.length) {
        visit(entry.children, entry.label === "Tools" ? path : [...path, entry.label], `${id}/${entry.id}`);
      }
    };
    visit(items[tool.id]?.length ? items[tool.id] : [{ id: tool.defaultAction ?? tool.id, label: tool.label, icon: tool.icon }], [tool.label], tool.id);
    return sections;
  });
}
function ToolAction({ item, label, active, icon, onSelect, dragRef, draggable }) {
  return /* @__PURE__ */ jsxs("button", { type: "button", ref: dragRef, "data-tool-action": item.id, "aria-label": `${label}: ${item.label}`, "aria-description": item.detail, "aria-pressed": active, onClick: onSelect, className: cn("flex min-h-9 w-full items-center gap-2 rounded-lg bg-accent/40 px-2.5 py-2 text-left text-sm hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring", draggable && "cursor-grab active:cursor-grabbing"), children: [
    icon,
    /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate", title: item.label, children: item.label }),
    item.detail && /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xs tabular-nums text-muted-foreground", children: item.detail })
  ] });
}
function DraggableToolAction(props) {
  const [, drag] = useDrag(() => ({
    type: ItemTypes.CANVAS_TOOL,
    item: () => {
      props.onDragStart?.(props.item.id);
      return { tool: props.item.id };
    }
  }), [props.item.id, props.onDragStart]);
  return /* @__PURE__ */ jsx(ToolAction, { ...props, draggable: true, dragRef: drag });
}
function CanvasToolbar({ activeTool, onToolSelect, onToolDragStart, className, modeControl, tools = TOOLBAR_TOOLS, dropdownItems = DROPDOWN_ITEMS_MAP, extraControls, navigation, paletteHost, paletteOpen = true, paletteSide = "left", onPaletteOpenChange, onPaletteSideChange, onPaletteWidthChange, browseState, onBrowseStateChange, toolPanel, renderActionIcon }) {
  const [localBrowse, setLocalBrowse] = useState({ category: null, query: "", scrollTop: 0 });
  const browse = browseState ?? localBrowse;
  const { category, query } = browse;
  const updateBrowse = onBrowseStateChange ?? setLocalBrowse;
  const search = useRef(null);
  const [listElement, setListElement] = useState(null);
  const returnFocus = useRef(null);
  const parametersTitle = useRef(null);
  const lastScrollSelection = useRef(null);
  const lastListElement = useRef(null);
  const hadCatalogue = useRef(false);
  const wasPaletteVisible = useRef(paletteOpen);
  const initialSelection = useRef(`${category}:${query}`);
  const catalogueId = useId();
  const dragAndDrop = Boolean(useContext(DndContext).dragDropManager);
  const parametersOpen = Boolean(toolPanel?.open);
  const parametersDocked = Boolean(toolPanel && "host" in toolPanel);
  const catalogueOpen = Boolean(category);
  const railWidth = RAIL_WIDTH;
  const sections = useMemo(() => sectionsFor(tools, dropdownItems), [tools, dropdownItems]);
  const utilityCategories = tools.filter((tool) => tool.hasDropdown && tool.placement === "utility");
  const directTools = tools.filter((tool) => !tool.hasDropdown);
  const normalizedQuery = query.trim().toLowerCase();
  const visible = useMemo(() => sections.map((section) => ({ ...section, items: section.items.filter((item) => `${section.label} ${item.label}`.toLowerCase().includes(normalizedQuery)) })).filter((section) => section.items.length), [sections, normalizedQuery]);
  useLayoutEffect(() => {
    if (parametersOpen && paletteOpen && !parametersDocked) parametersTitle.current?.focus({ preventScroll: true });
  }, [parametersOpen, parametersDocked, activeTool, paletteOpen]);
  useLayoutEffect(() => {
    if (paletteHost) onPaletteWidthChange?.(paletteOpen ? railWidth + (catalogueOpen ? CATALOG_WIDTH + 8 : 0) + (parametersOpen && !parametersDocked ? PARAMETERS_WIDTH + 8 : 0) : 0);
  }, [catalogueOpen, paletteHost, paletteOpen, onPaletteWidthChange, parametersOpen, parametersDocked, railWidth]);
  useLayoutEffect(() => {
    if (!listElement || !catalogueOpen) {
      lastListElement.current = null;
      lastScrollSelection.current = null;
      if (!catalogueOpen) hadCatalogue.current = false;
      return;
    }
    if (!paletteOpen) {
      wasPaletteVisible.current = false;
      return;
    }
    const selection = `${category}:${query}`;
    const newList = lastListElement.current !== listElement;
    const becameVisible = !wasPaletteVisible.current;
    lastListElement.current = listElement;
    wasPaletteVisible.current = true;
    if (newList && (hadCatalogue.current || initialSelection.current === selection) || becameVisible) {
      listElement.scrollTop = browse.scrollTop;
      lastScrollSelection.current = selection;
      hadCatalogue.current = true;
      return;
    }
    hadCatalogue.current = true;
    if (lastScrollSelection.current === selection) return;
    if (query || category === "all") {
      listElement.scrollTop = 0;
      lastScrollSelection.current = selection;
      return;
    }
    const section = Array.from(listElement.querySelectorAll("[data-tool-category]")).find((el) => el.dataset.toolCategory === category);
    if (section) listElement.scrollTop += section.getBoundingClientRect().top - listElement.getBoundingClientRect().top;
    lastScrollSelection.current = selection;
  }, [category, catalogueOpen, listElement, paletteOpen, query, sections]);
  const close = () => {
    updateBrowse({ category: null, query: "", scrollTop: 0 });
    returnFocus.current?.focus();
  };
  const closeParameters = () => {
    toolPanel?.onClose();
    requestAnimationFrame(() => search.current?.focus({ preventScroll: true }));
  };
  const open = (id, button) => {
    returnFocus.current = button;
    onPaletteOpenChange?.(true);
    updateBrowse((current) => ({ category: paletteOpen && current.category === id ? null : id, query: "", scrollTop: 0 }));
    requestAnimationFrame(() => search.current?.focus({ preventScroll: true }));
  };
  const toolButton = (label, icon, onClick, expanded, pressed, side = paletteSide === "left" ? "right" : "left", buttonKey = label) => /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { type: "button", size: "icon", variant: "ghost", "aria-label": label, "aria-expanded": expanded, "aria-pressed": pressed, "aria-controls": expanded === void 0 ? void 0 : catalogueId, onClick: (event) => onClick(event.currentTarget), className: cn("h-10 w-10 shrink-0 rounded-lg", (expanded || pressed) && "bg-accent"), children: renderActionIcon?.(buttonKey) ?? /* @__PURE__ */ jsx(Icon, { name: icon, className: "h-5 w-5" }) }) }),
    /* @__PURE__ */ jsx(TooltipContent, { side, size: "sm", children: label })
  ] }, buttonKey);
  const palette = /* @__PURE__ */ jsxs("div", { className: cn("h-full min-h-0 items-stretch gap-2 pointer-events-none", paletteOpen ? "flex" : "hidden", paletteSide === "right" && "flex-row-reverse"), "data-tool-palette": true, "data-palette-side": paletteSide, children: [
    /* @__PURE__ */ jsxs("div", { style: { width: railWidth }, className: "pointer-events-auto flex h-full shrink-0 flex-col items-center gap-1 rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-float", children: [
      /* @__PURE__ */ jsx("nav", { "aria-label": "Tool categories", className: "flex min-h-0 w-full flex-1 flex-col overflow-y-auto overflow-x-hidden", children: /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 flex-col items-center gap-1", children: [
        tools.filter((tool) => tool.hasDropdown && tool.placement !== "utility").map((tool) => toolButton(`${tool.label} tools`, tool.icon, (button) => open(tool.id, button), catalogueOpen && category === tool.id)),
        utilityCategories.length > 0 && /* @__PURE__ */ jsx("div", { role: "group", "aria-label": "Utilities", className: "flex flex-col items-center gap-1 border-t border-border pt-1", children: utilityCategories.map((tool) => toolButton(`${tool.label} tools`, tool.icon, (button) => open(tool.id, button), catalogueOpen && category === tool.id)) })
      ] }) }),
      /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 flex-col items-center gap-1", children: [
        onPaletteOpenChange && /* @__PURE__ */ jsx(PanelCloseButton, { title: "tool sidebar", onClose: () => onPaletteOpenChange(false) }),
        onPaletteSideChange && toolButton(`Move tool sidebar to ${paletteSide === "left" ? "right" : "left"}`, paletteSide === "left" ? "panel-right" : "panel-left", () => onPaletteSideChange(paletteSide === "left" ? "right" : "left"), void 0, void 0, paletteSide === "left" ? "right" : "left", "sidebar-placement")
      ] })
    ] }),
    catalogueOpen && /* @__PURE__ */ jsxs("section", { id: catalogueId, "aria-label": "Tool catalogue", className: "pointer-events-auto flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-float", style: { width: CATALOG_WIDTH, maxWidth: "calc(100vw - 88px)" }, onKeyDown: (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }, children: [
      /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center justify-between px-3 pt-2 pb-1", children: [
        /* @__PURE__ */ jsx("h2", { className: "text-sm font-medium", children: "Tools" }),
        /* @__PURE__ */ jsx(PanelCloseButton, { title: "tool catalogue", onClose: close })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "mx-3 mt-1 mb-2 flex shrink-0 items-center gap-2 rounded-lg bg-accent/60 px-2.5 py-2", children: [
        /* @__PURE__ */ jsx(Icon, { name: "search", className: "h-4 w-4 shrink-0 text-muted-foreground" }),
        /* @__PURE__ */ jsx("input", { ref: search, type: "search", "aria-label": "Search tools", placeholder: "Search all tools\u2026", value: query, onChange: (event) => {
          const nextQuery = event.target.value;
          updateBrowse((current) => ({ ...current, query: nextQuery, scrollTop: 0 }));
        }, className: "min-w-0 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground", onKeyDown: (event) => {
          if (event.key === "Escape") return;
          event.stopPropagation();
          if (event.key === "ArrowDown") {
            event.preventDefault();
            listElement?.querySelector("[data-tool-action]")?.focus();
          }
        } })
      ] }),
      /* @__PURE__ */ jsxs("div", { ref: setListElement, className: "relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3", onScroll: (event) => {
        if (!paletteOpen) return;
        const scrollTop = event.currentTarget.scrollTop;
        updateBrowse((current) => current.scrollTop === scrollTop ? current : { ...current, scrollTop });
      }, onKeyDown: (event) => {
        if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
        const buttons = [...event.currentTarget.querySelectorAll("[data-tool-action]")];
        const index = buttons.indexOf(document.activeElement);
        if (index < 0) return;
        event.preventDefault();
        event.stopPropagation();
        const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }, children: [
        visible.map((section) => /* @__PURE__ */ jsxs("section", { "data-tool-category": section.category, "aria-label": section.label, className: "pt-3 first:pt-1", children: [
          /* @__PURE__ */ jsx("h3", { className: "mb-2 truncate text-xs font-medium text-muted-foreground", title: section.label, children: section.label }),
          /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1", children: section.items.map((item) => {
            const Row = dragAndDrop && !item.opensPanel ? DraggableToolAction : ToolAction;
            return /* @__PURE__ */ jsx(Row, { item, label: section.label, active: activeTool === item.id, icon: renderActionIcon?.(item.id) ?? /* @__PURE__ */ jsx(Icon, { name: item.icon, className: "h-4 w-4 shrink-0" }), onSelect: () => {
              onToolSelect?.(item.id);
            }, onDragStart: onToolDragStart }, item.id);
          }) })
        ] }, section.id)),
        visible.length === 0 && /* @__PURE__ */ jsx("p", { role: "status", className: "py-6 text-center text-sm text-muted-foreground", children: "No tools found" })
      ] })
    ] }),
    toolPanel && !parametersDocked && /* @__PURE__ */ jsxs("section", { "aria-label": "Tool parameters", hidden: !parametersOpen, className: cn("pointer-events-auto min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-float", parametersOpen ? "flex" : "hidden"), style: { width: PARAMETERS_WIDTH, maxWidth: "calc(100vw - 88px)" }, onKeyDown: (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeParameters();
      }
    }, children: [
      /* @__PURE__ */ jsxs("header", { className: "flex h-10 shrink-0 items-center gap-2 border-b border-border px-3", children: [
        /* @__PURE__ */ jsx("h2", { ref: parametersTitle, tabIndex: -1, className: "min-w-0 flex-1 truncate text-sm font-medium outline-none", children: toolPanel.title }),
        /* @__PURE__ */ jsx(PanelCloseButton, { title: "tool parameters", onClose: closeParameters })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex min-h-0 flex-1 flex-col", children: toolPanel.content })
    ] })
  ] });
  return /* @__PURE__ */ jsxs(TooltipProvider, { delayDuration: 150, children: [
    paletteHost && createPortal(palette, paletteHost),
    parametersDocked && toolPanel?.host && createPortal(toolPanel.content, toolPanel.host),
    (directTools.length > 0 || extraControls || navigation || modeControl) && /* @__PURE__ */ jsxs("div", { role: "toolbar", "aria-label": "Canvas controls", className: cn("inline-flex max-w-full items-center gap-1 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-float", className), children: [
      navigation && [
        { label: "Select", icon: "mouse-pointer-2", pressed: !navigation.panMode, action: navigation.onSelect },
        { label: "Pan canvas", icon: "hand", pressed: navigation.panMode, action: navigation.onPan },
        { label: "Fit view", icon: "maximize", pressed: void 0, action: navigation.onFit },
        ...navigation.onAutoArrange ? [{ label: "Auto-arrange nodes", icon: "sparkles", pressed: void 0, action: navigation.onAutoArrange, disabled: navigation.autoArrangeDisabled }] : []
      ].map((control) => /* @__PURE__ */ jsxs(Tooltip, { children: [
        /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { type: "button", size: "icon", variant: control.pressed ? "secondary" : "ghost", "aria-label": control.label, "aria-pressed": control.pressed, disabled: "disabled" in control ? control.disabled : void 0, onClick: control.action, className: "h-10 w-10 shrink-0 rounded-lg", children: /* @__PURE__ */ jsx(Icon, { name: control.icon, className: "h-5 w-5" }) }) }),
        /* @__PURE__ */ jsx(TooltipContent, { side: "top", size: "sm", children: control.label })
      ] }, control.label)),
      extraControls,
      directTools.length > 0 && /* @__PURE__ */ jsx("div", { role: "group", "aria-label": "Canvas utilities", className: cn("flex shrink-0 items-center gap-1", (extraControls || navigation) && "ml-1 border-l border-border pl-1"), children: directTools.map((tool) => toolButton(tool.label, tool.icon, () => onToolSelect?.(tool.id), void 0, activeTool === tool.id, "top")) }),
      modeControl && /* @__PURE__ */ jsx("div", { className: "ml-1 flex shrink-0 items-center border-l border-border pl-1", children: modeControl })
    ] })
  ] });
}

export {
  CanvasToolbar
};
//# sourceMappingURL=chunk-V5JECMWD.js.map